import { type TopPartiesResponse } from "../models/profile/search";
import { type UserInfoType } from "../models/profile/user-info";
import {
  type UserListResponse,
  type SearchItemListResponse,
} from "../models/secure-chat";
import { authorizedRequest, unAuthorizedRequest } from "../utils/axios";
import { API_ENDPOINT } from "./endpoint";

export interface UserNoneResponse {
  nonce: string;
}

interface TokenRequest {
  wallet_address: string;
  message: string;
  signature: string;
}

export interface TokenResponse {
  token: string;
}

export interface DeviceTokenRequest {
  device_token: string;
  app_name?: string;
  app_version?: string;
  platform?: string;
}

interface AddUserRequest {
  wallet_address: string;
  display_name: string | null;
  message: string;
  signature: string;
}

export type Links = "twitter" | "facebook" | "discord" | "telegram" | "website";

export interface UpdateUserRequest {
  display_name: string | null;
  bio: string;
  description: string;
  social_networks: Record<Links, string>;
}
export const userApi = {
  requestToken: async (body: TokenRequest): Promise<TokenResponse> =>
    await unAuthorizedRequest.post(
      `${API_ENDPOINT.PUBLIC_USER}/request_auth_token`,
      body
    ),

  getNonce: async (walletAddress: string): Promise<UserNoneResponse> =>
    await unAuthorizedRequest.get(
      `${API_ENDPOINT.USER}/nonce?wallet_address=${walletAddress}`
    ),

  getUnAuthorizedUser: async (
    walletAddress: string,
    signal?: AbortSignal
  ): Promise<UserInfoType> =>
    await unAuthorizedRequest.get(
      `${API_ENDPOINT.PUBLIC_USER}/${walletAddress}`,
      { signal }
    ),

  getAuthorizedUser: async (walletAddress: string): Promise<UserInfoType> =>
    await authorizedRequest.get(`${API_ENDPOINT.USER}/${walletAddress}`),

  addUser: async (body: AddUserRequest): Promise<UserInfoType> =>
    await unAuthorizedRequest.post(API_ENDPOINT.PUBLIC_USER, body),

  updateUser: async (
    body: UpdateUserRequest,
    id: string
  ): Promise<UserInfoType> =>
    await authorizedRequest.patch(`${API_ENDPOINT.USER}/${id}`, body),

  topParties: async (): Promise<TopPartiesResponse> => {
    return await authorizedRequest.get(`${API_ENDPOINT.ROOM}/top_parties`);
  },

  searchUsers: async (
    params: Record<string, string>,
    signal?: AbortSignal
  ): Promise<SearchItemListResponse> => {
    const query = "?" + new URLSearchParams(params).toString();
    return await authorizedRequest.get(`${API_ENDPOINT.SEARCH}${query}`, {
      signal,
    });
  },

  // params: search, community_id, room_id, include_readonly (true or false)
  suggestedUsers: async (
    params: Record<string, any>,
    signal?: AbortSignal
  ): Promise<UserListResponse> => {
    const query = "?" + new URLSearchParams(params).toString();
    return await authorizedRequest.get(
      `${API_ENDPOINT.USER}/suggested${query}`,
      { signal }
    );
  },

  addNewDevice: async (body: DeviceTokenRequest): Promise<any> =>
    await authorizedRequest.post(`${API_ENDPOINT.DEVICES}`, body),

  userBlockList: async (
    params: Record<string, any>,
    signal?: AbortSignal
  ): Promise<any> => {
    const query = "?" + new URLSearchParams(params).toString();
    return await authorizedRequest.get(`${API_ENDPOINT.RELATIONSHIP}${query}`, {
      signal,
    });
  },
};
